import React, { ReactElement, ReactNode, useEffect, useMemo } from "react";
import {
  ChartContainerProProps,
  ChartDataProviderPro,
  ChartsAxisHighlight,
  ChartsClipPath,
  ChartsLegend,
  ChartsReferenceLine,
  ChartsReferenceLineProps,
  ChartsSurface,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  ChartZoomSlider,
  ContinuousColorLegend,
  LinePlot,
  LineSeriesType,
  useChartProApiRef,
  XAxis,
  YAxis,
} from "@mui/x-charts-premium";
import {
  DurationSeries,
  DurationSeriesImpl,
} from "firefly-shared-ts-timeseries";
import { Box, SxProps } from "@mui/material";
import {
  MakeOptional,
  TimeLineAnnotation,
  TimeLineAnnotationProps,
  useSxMergeStrict,
} from "shared-ts-mui";
import { ConditionalWrap } from "shared-ts-ui-react";
import { ZoomData } from "@mui/x-charts/internals";
import { useYAxis } from "./yAxis";
import { useXAxis } from "./xAxis";
import { useSeries } from "./series";
import { resolveTimeAxisZoom, TimeAxisZoomOptions } from "./zoom";
import { useResizeDetector } from "react-resize-detector";
import { Duration } from "luxon";
import { _throw, IllegalArgumentError, notUndef } from "shared-ts-utils-core";
import { QueryResultMetaData } from "shared-ts-ui-loading";

export type MuiDurationSeriesOptions = Omit<
  MakeOptional<LineSeriesType, "type">,
  "data"
>;

export interface DurationSeriesMuiChartProps {
  queryResultMetaData?: QueryResultMetaData;
  durationSeries: Array<DurationSeries<number | null>> | undefined;
  durationSeriesOptions?: Array<MuiDurationSeriesOptions>;
  referenceLines?: Array<ChartsReferenceLineProps>;
  annotations?: Array<TimeLineAnnotationProps>;
  yAxisConfig?: Array<YAxis<"linear">>;
  xAxisConfig?: XAxis<"linear">;
  xAxisZoom?: boolean | Partial<TimeAxisZoomOptions>;
  margin?: Partial<ChartContainerProProps["margin"]> | number;
  hideLegend?: boolean;
  sx?: SxProps;
  children?: ReactNode;
}

export function DurationSeriesMuiChart(
  props: DurationSeriesMuiChartProps
): ReactElement {
  const {
    durationSeries,
    durationSeriesOptions,
    xAxisConfig,
    yAxisConfig,
    xAxisZoom,
  } = props;
  const hideLegend = props.hideLegend ?? false;
  const zoomEnabled =
    (props.xAxisConfig?.zoom === true ||
      props.xAxisConfig?.zoom !== undefined) ??
    false;

  const { ref, width } = useResizeDetector({
    handleHeight: false,
  });
  const xAxisMin: Duration = useMemo(() => {
    return Duration.fromMillis(0);
  }, []);
  const xAxisMax: Duration = useMemo(() => {
    if (xAxisConfig?.max !== undefined) {
      return typeof xAxisConfig?.max === "number"
        ? Duration.fromMillis(xAxisConfig.max)
        : (xAxisConfig.max as unknown) instanceof Date
        ? _throw(
            new IllegalArgumentError(
              "Date not supported as max value on xAxis: " +
                JSON.stringify(xAxisConfig.max)
            )
          )
        : xAxisConfig.max;
    }

    return (
      notUndef(durationSeries, (it) =>
        DurationSeriesImpl.getMaxDuration(it, xAxisMin)
      ) ?? xAxisMin
    );
  }, [durationSeries, xAxisConfig?.max, xAxisMin]);

  const xAxis = useXAxis(
    xAxisMin,
    xAxisMax,
    "hour",
    width,
    xAxisConfig,
    xAxisZoom
  );

  const yAxis = useYAxis(durationSeries, yAxisConfig);
  const series = useSeries(durationSeries, durationSeriesOptions);
  const [zoomData, setZoomData] = React.useState<ZoomData[]>([
    {
      axisId: "time",
      start: 0,
      end: 100,
    },
  ]);
  const clipPathId = React.useId();
  const apiRef = useChartProApiRef<"line" | "bar">();

  const handleZoomChange = (zoomData: ZoomData[]) => {
    setZoomData(zoomData);
  };

  useEffect(() => {
    const doAsync = async () => {
      if (zoomEnabled) {
        const zoom = resolveTimeAxisZoom(
          xAxisMin,
          xAxisMax,
          xAxisZoom,
          "hour",
          width,
          "start"
        );
        if (zoom !== undefined) {
          apiRef.current?.setAxisZoomData("time", {
            axisId: "time",
            start: zoom.start,
            end: zoom.end,
          });
        }
      }
    };

    doAsync();
  }, [apiRef, xAxisMin, xAxisMax, xAxisZoom, width, zoomEnabled, xAxis]);

  const chartSurfaceSx = useSxMergeStrict(
    () => [
      {
        ".MuiLineElement-root": {
          strokeWidth: 3,
          filter: "drop-shadow(2px 3px 3px rgba(0, 0, 0, 0.9))",
        },
      },
    ],
    []
  );
  const rootSx = useSxMergeStrict(
    () => [
      {
        flexGrow: 1,
        minHeight: 0,
        minWidth: 0,
        position: "relative",
        display: "flex",
        flexDirection: "row",
      },
      props.sx,
    ],
    [props.sx]
  );

  return (
    <Box ref={ref} sx={rootSx}>
      <ChartDataProviderPro
        apiRef={apiRef}
        margin={props.margin}
        xAxis={xAxis}
        yAxis={yAxis}
        series={series}
        zoomData={zoomData}
        onZoomChange={handleZoomChange}
      >
        <ChartsSurface sx={chartSurfaceSx}>
          <ConditionalWrap
            condition={zoomEnabled}
            wrap={(children) => (
              <g clipPath={`url(#${clipPathId})`}>{children}</g>
            )}
          >
            <LinePlot />
            {props.annotations?.map((annotation) => (
              <TimeLineAnnotation {...annotation} />
            ))}
            <ChartsAxisHighlight x={"line"} />
          </ConditionalWrap>
          <ChartsXAxis />
          {zoomEnabled && <ChartZoomSlider />}
          {zoomEnabled && <ChartsClipPath id={clipPathId} />}
          <ChartsYAxis />
          {props.referenceLines?.map((it, index) => {
            return <ChartsReferenceLine {...it} key={it.label ?? index} />;
          })}
          {props.children}
          <ChartsTooltip />
        </ChartsSurface>
        {!hideLegend && (
          <ChartsLegend
            slots={{ legend: ContinuousColorLegend }}
            slotProps={{
              legend: {
                axisDirection: "y",
                direction: "vertical",
                thickness: 12,
                labelPosition: "end",
                reverse: false,
                sx: { height: "calc(100% - 102px)" },
              },
            }}
          />
        )}
      </ChartDataProviderPro>
    </Box>
  );
}
